import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FolderTree, Plus, ArrowUp, ArrowDown, Trash2, Check, Pencil, Loader2 } from "lucide-react";
import aapi, { formatApiError } from "./aapi";
import { PageHeader, EmptyState, inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

export default function AdminCategories() {
  const [cats, setCats] = useState(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => aapi.get("/categories").then(({ data }) => setCats(data)).catch(() => setCats([]));
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await aapi.post("/categories", { name: newName });
      toast.success("Category added");
      setNewName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const update = async (id, updates, msg) => {
    try {
      await aapi.put(`/categories/${id}`, updates);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Services in it keep their label.`)) return;
    await aapi.delete(`/categories/${c.id}`).then(() => {
      toast.success("Category deleted");
      load();
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const move = async (idx, dir) => {
    const arr = [...cats];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    const a = arr[idx], b = arr[j];
    setCats(arr.with ? arr.with(idx, b).with(j, a) : arr);
    await Promise.all([
      aapi.put(`/categories/${a.id}`, { sort: b.sort }),
      aapi.put(`/categories/${b.id}`, { sort: a.sort }),
    ]).catch(() => {});
    load();
  };

  return (
    <div data-testid="admin-categories-page">
      <PageHeader title="Categories" sub="The taxonomy behind every service." />

      <form onSubmit={add} className="mb-8 flex max-w-lg gap-3">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category name"
          required
          minLength={2}
          className={inputCls}
          data-testid="category-name-input"
        />
        <button type="submit" disabled={busy} data-testid="category-add-button"
          className="flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark px-5 text-sm font-bold text-black disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add
        </button>
      </form>

      {cats === null ? (
        <div className="flex justify-center py-24">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
        </div>
      ) : cats.length === 0 ? (
        <EmptyState icon={FolderTree} title="No categories" sub="Add the first one above." testid="categories-empty" />
      ) : (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="max-w-3xl space-y-3" data-testid="categories-list">
          {cats.map((c, i) => (
            <div key={c.id} data-testid={`category-row-${c.name}`}
              className={`flex items-center gap-3 rounded-2xl border px-5 py-4 transition-colors duration-300 ${
                c.enabled ? "border-white/10 bg-panel/80" : "border-white/5 bg-panel/40 opacity-60"
              }`}>
              <span className="flex gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`category-up-${c.name}`}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 text-neutral-500 hover:border-gold/50 hover:text-goldlight disabled:opacity-30">
                  <ArrowUp size={12} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === cats.length - 1} aria-label="Move down" data-testid={`category-down-${c.name}`}
                  className="flex h-7 w-7 items-center justify-center rounded-lg border border-white/10 text-neutral-500 hover:border-gold/50 hover:text-goldlight disabled:opacity-30">
                  <ArrowDown size={12} />
                </button>
              </span>

              {editingId === c.id ? (
                <div className="flex flex-1 gap-2">
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} className={`${inputCls} py-2`} data-testid="category-rename-input" />
                  <button
                    onClick={() => { update(c.id, { name: editName }, "Category renamed — services updated too"); setEditingId(null); }}
                    aria-label="Save rename" data-testid="category-rename-save"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold text-black">
                    <Check size={15} />
                  </button>
                </div>
              ) : (
                <span className="flex-1 text-sm font-medium text-neutral-100">{c.name}</span>
              )}

              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                c.enabled ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-white/15 bg-white/5 text-neutral-500"
              }`}>
                {c.enabled ? "Enabled" : "Disabled"}
              </span>

              <button onClick={() => update(c.id, { enabled: !c.enabled }, c.enabled ? "Category disabled" : "Category enabled")}
                data-testid={`category-toggle-${c.name}`}
                className="rounded-lg border border-white/10 px-3 py-1.5 text-[11px] font-medium text-neutral-400 transition-colors hover:border-gold/40 hover:text-neutral-100">
                {c.enabled ? "Disable" : "Enable"}
              </button>
              <button onClick={() => { setEditingId(c.id); setEditName(c.name); }} aria-label="Rename" data-testid={`category-rename-${c.name}`}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-gold/30 text-goldlight transition-colors hover:bg-gold hover:text-black">
                <Pencil size={13} />
              </button>
              <button onClick={() => remove(c)} aria-label="Delete" data-testid={`category-delete-${c.name}`}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-400/30 text-rose-300 transition-colors hover:bg-rose-400/10">
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
